import { http, HttpResponse } from 'msw';
import { DEMO_EXCHANGE_ACCOUNT } from '../demo-fixtures';

const BASE = import.meta.env?.VITE_API_BASE_URL ?? 'http://localhost:8000';

export const demoExchangeAccountsHandlers = [
  // ── List accounts ─────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/exchange-accounts`, () => {
    return HttpResponse.json({
      items: [DEMO_EXCHANGE_ACCOUNT],
      total: 1,
      limit: 50,
      offset: 0,
    });
  }),

  // ── Get account ───────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/exchange-accounts/:id`, () => {
    return HttpResponse.json(DEMO_EXCHANGE_ACCOUNT);
  }),

  // ── Create account — succeeds but returns demo-only response ──────────────
  http.post(`${BASE}/api/v1/exchange-accounts`, async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    return HttpResponse.json(
      {
        ...DEMO_EXCHANGE_ACCOUNT,
        id: 99,
        label: (body['label'] as string) ?? '[DEMO] New Account',
        platform: (body['platform'] as string) ?? 'BINANCE',
        created_at: new Date().toISOString(),
      },
      { status: 201 },
    );
  }),

  // ── Update account ────────────────────────────────────────────────────────
  http.patch(`${BASE}/api/v1/exchange-accounts/:id`, async ({ request }) => {
    const body = (await request.json()) as object;
    return HttpResponse.json({
      ...DEMO_EXCHANGE_ACCOUNT,
      ...(body as Partial<typeof DEMO_EXCHANGE_ACCOUNT>),
      updated_at: new Date().toISOString(),
    });
  }),

  // ── Delete account ────────────────────────────────────────────────────────
  http.delete(`${BASE}/api/v1/exchange-accounts/:id`, () => {
    return new HttpResponse(null, { status: 204 });
  }),

  // ── Connectivity test ─────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/exchange-accounts/:id/test`, () => {
    return HttpResponse.json({
      account_id: 1,
      platform: 'BINANCE',
      success: true,
      message: 'Connection successful. Market data API responded in 38ms. (DEMO)',
    });
  }),

  // ── Balance ───────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/exchange-accounts/:id/balance`, () => {
    return HttpResponse.json({
      account_id: 1,
      platform: 'BINANCE',
      total_usdt: '47320.00',
      available_usdt: '23750.00',
      in_positions_usdt: '23570.00',
      assets: [
        { asset: 'USDT', free: '23750.00', locked: '0.00' },
        { asset: 'BTC', free: '0.057', locked: '0.000' },
        { asset: 'SOL', free: '0.00', locked: '20.50' },
      ],
      fetched_at: new Date().toISOString(),
    });
  }),

  // ── Key rotation ─────────────────────────────────────────────────────────
  http.put(`${BASE}/api/v1/exchange-accounts/:id/rotate-keys`, () => {
    return HttpResponse.json({
      account_id: 1,
      platform: 'BINANCE',
      rotated: true,
      message: 'API keys rotated (DEMO — no real change)',
    });
  }),

  // ── Resolved config ───────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/exchange-accounts/:id/config`, () => {
    return HttpResponse.json({
      account_id: 1,
      platform: 'BINANCE',
      config: {
        recv_window: 5000,
        rate_limit_buffer: 0.8,
        default_order_type: 'LIMIT',
        enable_margin: false,
      },
    });
  }),

  // ── Live status ───────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/exchange-accounts/:id/live-status`, () => {
    return HttpResponse.json({
      account_id: 1,
      authorized: false,
      bot_id: null,
      ceremony_id: null,
      ceremony_at: null,
      config_checksum: 'demo-checksum-abc123',
      expires_at: null,
    });
  }),

  // ── Live history ──────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/exchange-accounts/:id/live-history`, () => {
    return HttpResponse.json({ items: [], total: 0, limit: 20, offset: 0 });
  }),

  // ── Exchange metadata (intervals) ────────────────────────────────────────
  http.get(`${BASE}/api/v1/exchange-accounts/:id/metadata`, () => {
    return HttpResponse.json({
      account_id: 1,
      platform: 'BINANCE',
      supported_intervals: [
        '1m',
        '3m',
        '5m',
        '15m',
        '30m',
        '1h',
        '2h',
        '4h',
        '6h',
        '8h',
        '12h',
        '1d',
      ],
      supported_order_types: ['MARKET', 'LIMIT', 'STOP_LIMIT'],
    });
  }),
];
